<template>
  <div>
    <b-card title="Area" class="area-background-color">
      <b-list-group>
        <b-list-group-item active>
          Please provide either radius or zip codes
        </b-list-group-item>
        <b-list-group-item
          v-for="districtCode in combinedDistrictCodes"
          :key="districtCode.countryCode"
        >
          <DistrictCodeItem
            v-bind="districtCode"
            :assignmentTermId="assignmentTermId"
            :updateAllowed="!updateForm && !radiusWasSet"
            :afterDestroy="afterDestroy"
          />
        </b-list-group-item>

        <b-list-group-item>
          <b-row align-v="center">
            <b-col md="auto">
              <b-form-group label="Country:" label-for="countryToAdd">
                <b-form-select
                  v-model="assignmentTermForm.districtCodesForm.countryCode"
                  :options="countriesListForSelect"
                  class="mb-3"
                  :disabled="updateForm || radiusWasSet"
                >
                  <template slot="first">
                    <option slot="first" :value="null"
                      >Please choose country</option
                    >
                  </template>
                </b-form-select>

                <div
                  class="display-inline-block"
                  v-if="assignmentTermId"
                  :id="'addCountry-' + assignmentTermId"
                >
                  <b-btn
                    ref="addCountryBtn"
                    v-promise-btn
                    class="btn btn-success"
                    size="sm"
                    :disabled="updateForm || !districtCodeCanBeAdded"
                    @click="createDistrictCode"
                  >
                    <font-awesome-icon icon="plus-square" />
                    Add country
                  </b-btn>
                  <b-popover
                    :target="'addCountry-' + assignmentTermId"
                    triggers="hover focus"
                    :disabled="updateForm || !radiusWasSet"
                    content="Radius was already set"
                  >
                  </b-popover>
                </div>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group label="Zip codes:" label-for="zipCodesList">
                <b-form-textarea
                  v-model="zipCodesString"
                  placeholder="Comma separated zip codes"
                  :rows="3"
                  :max-rows="3"
                  :disabled="updateForm || radiusWasSet"
                >
                </b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-checkbox
                v-model="assignmentTermForm.districtCodesForm.entireCountry"
                :value="true"
                :unchecked-value="false"
                :disabled="updateForm || radiusWasSet"
              >
                Countrywide
              </b-form-checkbox>
            </b-col>
          </b-row>
        </b-list-group-item>
      </b-list-group>
    </b-card>
  </div>
</template>

<script>
import DistrictCodeItem from '@/components/DistrictCodeItem'
import CountriesProvider from '@/services/CountriesProvider'
import DistrictCodesProvider from '@/services/DistrictCodesProvider'

export default {
  name: 'DistrictCodesBar',
  props: {
    assignmentTermId: Number,
    districtCodes: Array,
    radiusWasSet: Boolean,
    assignmentTermForm: Object,
    updateForm: { type: Boolean, default: false }
  },
  data () {
    return {
      combinedDistrictCodes: []
    }
  },
  components: {
    DistrictCodeItem
  },
  mounted: function () {
    if (this.districtCodes) {
      this.combinedDistrictCodes = this.districtCodes.reduce(
        (res, districtCode) => {
          let combinedDistrictCode = res.find(
            item => item.countryCode === districtCode.country_code
          )
          if (combinedDistrictCode) {
            combinedDistrictCode.zipCodesList.push(districtCode.zipcode)
          } else {
            combinedDistrictCode = {
              assignmentTermId: this.assignmentTermId,
              countryCode: districtCode.country_code,
              countryName: CountriesProvider.nameForCode(
                districtCode.country_code
              ),
              zipCodesList: [districtCode.zipcode]
            }
            res.push(combinedDistrictCode)
          }
          return res
        },
        []
      )
    }
  },
  computed: {
    countriesListForSelect () {
      let exceptList = this.combinedDistrictCodes.map(dc => dc.countryCode)
      return CountriesProvider.forSelect().filter(country => {
        return exceptList.indexOf(country.value) < 0
      })
    },
    districtCodeFormValid () {
      if (this.assignmentTermForm.districtCodesForm.countryCode) {
        return (
          !!this.assignmentTermForm.districtCodesForm.zipCodesList.length ||
          this.assignmentTermForm.districtCodesForm.entireCountry
        )
      } else {
        return false
      }
    },
    districtCodeCanBeAdded () {
      return this.districtCodeFormValid && !this.radiusWasSet
    },
    zipCodesString: {
      get: function () {
        return this.assignmentTermForm.districtCodesForm.zipCodesList.join(
          ', '
        )
      },
      set: function (zipCodesString) {
        this.assignmentTermForm.districtCodesForm.zipCodesList = (() => {
          if (zipCodesString === '') {
            return []
          } else {
            return zipCodesString.split(', ')
          }
        })()
      }
    }
  },
  methods: {
    createDistrictCode (e) {
      e.preventDefault()
      let params = Object.assign(this.assignmentTermForm.districtCodesForm, {
        assignmentTermId: this.assignmentTermId
      })
      return new Promise(resolve => {
        DistrictCodesProvider.create(params)
          .then(result => {
            this.putDistrictCodeInCollection(params)
            this.clearDistrictCodeForm()
          })
          .then(response => resolve(resolve))
      })
    },
    putDistrictCodeInCollection (attributes) {
      attributes['countryName'] = CountriesProvider.nameForCode(
        attributes.countryCode
      )
      this.combinedDistrictCodes.push(attributes)
    },
    clearDistrictCodeForm () {
      this.assignmentTermForm.districtCodesForm = {
        countryCode: null,
        zipCodesList: [],
        entireCountry: false
      }
    },
    afterDestroy (districtCodeItem) {
      const index = this.combinedDistrictCodes
        .map(code => code.countryCode)
        .indexOf(districtCodeItem.countryCode)
      this.combinedDistrictCodes.splice(index, 1)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.area-background-color {
  background-color: #e6f1ff;
}

.display-inline-block {
  display: inline-block;
}
</style>
